import type { PracticeMode, PracticeScope } from '@/lib/practice/session-types';
import {
  scopeIcon,
  scopeLabel,
} from '@/components/features/mindspace/practice/utils';

/**
 * The "Practicing now" row pinned above the date groups — ROUTE_INVENTORY
 * §2.11 #18.
 *
 * Distinct from `HistoryRow` in kind, not just in styling: it is not a button.
 * There is nowhere to navigate to — the session it describes is already on
 * screen — so it carries no click target, and the ping dot is the only thing
 * that says "live" rather than "finished".
 */
export function ActiveSessionRow({
  active,
}: {
  active: { scope: PracticeScope; mode: PracticeMode };
}) {
  const Icon = scopeIcon(active.scope);
  return (
    <div className='px-2 pt-2'>
      <div className='relative flex items-center gap-2 overflow-hidden rounded-md border border-brand-200 bg-brand-50/70 px-2.5 py-2 dark:border-primary/50 dark:bg-tint-brand'>
        {/* Subtle left ribbon to mark the active row */}
        <span className='absolute inset-y-0 left-0 w-0.5 bg-brand-500 dark:bg-primary' />
        <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-card text-brand-600 shadow-sm dark:bg-muted dark:text-primary'>
          <Icon className='h-3.5 w-3.5' />
        </span>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-1.5'>
            <span className='truncate text-xs font-semibold text-brand-900 dark:text-tint-brand-foreground'>
              {scopeLabel(active.scope)}
            </span>
            {/* Full alpha. `text-brand-700/70` measured 3.44:1 on this row's
                own brand-tinted fill — the mode is already subordinated by
                weight and position, and fading it as well broke AA. */}
            <span className='flex-shrink-0 text-xs text-brand-700 dark:text-tint-brand-foreground'>
              {active.mode === 'flashcard' ? 'Flashcards' : 'Quiz'}
            </span>
          </div>
          <div className='mt-0.5 flex items-center gap-1 text-xs text-brand-700 dark:text-tint-brand-foreground'>
            <span className='relative flex h-1.5 w-1.5'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-70 dark:bg-primary' />
              <span className='relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-500 dark:bg-primary' />
            </span>
            Practicing now
          </div>
        </div>
      </div>
    </div>
  );
}
